/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import { DotLottie } from '@lottiefiles/dotlottie-web';

const DEFER_DESTROY_TIMEOUT_MS = 8000;

// Ported from admin-v4-ui's src/pages/hooks/useDotLottie.js so both apps
// render ECAL's standard loading connector the same way.
function useDotLottie({ src, loop = true, autoplay = true } = {}) {
    const canvasRef = useRef(null);
    const isUnmountingRef = useRef(false);

    // Must stay declared before the effect below — React cleans up effects
    // in declaration order, so this always sets the ref before that one reads it.
    useEffect(() => () => {
        isUnmountingRef.current = true;
    }, []);

    useEffect(() => {
        if (!canvasRef.current) return undefined;

        const dotLottie = new DotLottie({
            canvas: canvasRef.current,
            src,
            loop,
            autoplay,
        });

        let hasSettled = false;
        function markSettled() {
            hasSettled = true;
        }
        dotLottie.addEventListener('load', markSettled);
        dotLottie.addEventListener('loadError', markSettled);

        return () => {
            dotLottie.removeEventListener('load', markSettled);
            dotLottie.removeEventListener('loadError', markSettled);

            // Destroy right away on a src/loop/autoplay change (not
            // unmounting) — a new instance is about to reuse this canvas.
            if (hasSettled || !isUnmountingRef.current) {
                dotLottie.destroy();
                return;
            }

            // Otherwise wait for the fetch to settle on its own —
            // destroy() would abort it, and that abort always logs.
            function destroyOnceSettled() {
                dotLottie.removeEventListener('load', destroyOnceSettled);
                dotLottie.removeEventListener('loadError', destroyOnceSettled);
                clearTimeout(fallbackTimer);
                dotLottie.destroy();
            }
            // Safety net: neither event is guaranteed if the request
            // hangs, so force cleanup after a timeout regardless.
            const fallbackTimer = setTimeout(destroyOnceSettled, DEFER_DESTROY_TIMEOUT_MS);
            dotLottie.addEventListener('load', destroyOnceSettled);
            dotLottie.addEventListener('loadError', destroyOnceSettled);
        };
    }, [src, loop, autoplay]);

    return { canvasRef };
}

export default useDotLottie;
